import React, { useState, useEffect } from 'react';
import {
  Card, Descriptions, Tag, Table, Timeline, Button, Space,
  Select, Popconfirm, message, Typography, Row, Col, Spin, Statistic, Modal
} from 'antd';
import {
  ArrowLeftOutlined, DeleteOutlined, MailOutlined, SaveOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { usersApi } from 'api/users';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const STATUS_COLORS: Record<string, string> = {
  free: 'default',
  professional: 'green',
  enterprise: 'blue',
  free_trial: 'gold',
};

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [editStatus, setEditStatus] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const res = await usersApi.getDetail(id!);
      setData(res.data);
      setEditStatus(res.data.user.status);
    } catch (err) {
      message.error('Failed to fetch user details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDetail(); }, [id]);

  const handleSaveStatus = async () => {
    if (!editStatus || editStatus === data.user.status) return;
    setSaving(true);
    try {
      await usersApi.update(id!, { status: editStatus });
      message.success('Status updated');
      fetchDetail();
    } catch (err: any) {
      message.error(err.response?.data?.messages?.[0] || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await usersApi.delete(id!);
      message.success('User deleted');
      navigate('/users');
    } catch (err: any) {
      message.error(err.response?.data?.messages?.[0] || 'Delete failed');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data) return null;

  const { user, collections, total_collections, total_plant_items, total_animal_items, total_activities, recent_activities } = data;

  const collectionColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Type', dataIndex: 'type', key: 'type', render: (t: string) => <Tag>{t}</Tag> },
    { title: 'Items', dataIndex: 'item_count', key: 'item_count' },
    { title: 'Shared', dataIndex: 'shared_count', key: 'shared_count' },
    { title: 'Created', dataIndex: 'created_at', key: 'created_at',
      render: (d: string) => dayjs(d).format('YYYY-MM-DD') },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/users')}>
          Back to Users
        </Button>
      </Space>

      <Row gutter={[16, 16]}>
        {/* User Profile */}
        <Col xs={24} lg={16}>
          <Card title="User Profile">
            <Descriptions column={{ xs: 1, sm: 2 }} size="small">
              <Descriptions.Item label="Email">{user.email}</Descriptions.Item>
              <Descriptions.Item label="Name">{user.name || '-'}</Descriptions.Item>
              <Descriptions.Item label="Username">{user.username || '-'}</Descriptions.Item>
              <Descriptions.Item label="Region">{user.region || '-'}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={STATUS_COLORS[user.status]}>{user.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Admin">{user.is_admin ? 'Yes' : 'No'}</Descriptions.Item>
              <Descriptions.Item label="Email Verified">{user.email_verified ? 'Yes' : 'No'}</Descriptions.Item>
              <Descriptions.Item label="Joined">{dayjs(user.created_at).format('YYYY-MM-DD HH:mm')}</Descriptions.Item>
              <Descriptions.Item label="Last Active">
                {user.last_activity_date ? dayjs(user.last_activity_date).format('YYYY-MM-DD HH:mm') : 'Never'}
              </Descriptions.Item>
              <Descriptions.Item label="Sign-ins">{user.sign_in_count}</Descriptions.Item>
              <Descriptions.Item label="Level">{user.level}</Descriptions.Item>
              <Descriptions.Item label="Points">{user.points}</Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* Subscription Info + Actions */}
        <Col xs={24} lg={8}>
          <Card title="Subscription" style={{ marginBottom: 16 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Plan">{user.status}</Descriptions.Item>
              <Descriptions.Item label="Stripe Customer">{user.stripe_customer || '-'}</Descriptions.Item>
              <Descriptions.Item label="Stripe Sub">{user.stripe_subscription || '-'}</Descriptions.Item>
              <Descriptions.Item label="Expires">
                {user.subscription_expire_at ? dayjs(user.subscription_expire_at).format('YYYY-MM-DD') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Auto-Renew">
                {user.subscription_auto_renew_at ? dayjs(user.subscription_auto_renew_at).format('YYYY-MM-DD') : '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Actions">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Change Status</Text>
                <Space>
                  <Select
                    value={editStatus}
                    onChange={setEditStatus}
                    style={{ width: 150 }}
                    options={[
                      { label: 'Free', value: 'free' },
                      { label: 'Professional', value: 'professional' },
                      { label: 'Enterprise', value: 'enterprise' },
                      { label: 'Free Trial', value: 'free_trial' },
                    ]}
                  />
                  <Button
                    icon={<SaveOutlined />}
                    type="primary"
                    loading={saving}
                    onClick={handleSaveStatus}
                    disabled={editStatus === user.status}
                  >
                    Save
                  </Button>
                </Space>
              </div>
              <Button
                icon={<MailOutlined />}
                onClick={() => navigate('/email', { state: { selectedUserIds: [user._id] } })}
              >
                Send Email
              </Button>
              <Popconfirm
                title="Delete this user?"
                description="This will cascade-delete collections, items, activities, follows, error reports, and suggestions."
                onConfirm={handleDelete}
                okText="Delete"
                okType="danger"
              >
                <Button danger icon={<DeleteOutlined />}>Delete User</Button>
              </Popconfirm>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Stats */}
      <Row gutter={[16, 16]} style={{ margin: '16px 0' }}>
        <Col xs={12} sm={6}>
          <Card><Statistic title="Collections" value={total_collections} /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card><Statistic title="Plant Items" value={total_plant_items} /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card><Statistic title="Animal Items" value={total_animal_items} /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card><Statistic title="Activities" value={total_activities} /></Card>
        </Col>
      </Row>

      {/* Collections */}
      <Card title="Collections" style={{ marginBottom: 16 }}>
        <Table
          dataSource={collections}
          columns={collectionColumns}
          rowKey="_id"
          size="small"
          pagination={false}
        />
      </Card>

      {/* Activity Timeline */}
      <Card title="Recent Activity (Last 50)">
        {recent_activities && recent_activities.length > 0 ? (
          <Timeline
            items={recent_activities.map((a: any) => ({
              children: (
                <div>
                  <Text strong>{a.activity_type}</Text>
                  <br />
                  <Text type="secondary">{dayjs(a.created_at).format('YYYY-MM-DD HH:mm')}</Text>
                  {a.metadata?.species_name && (
                    <Text style={{ marginLeft: 8 }}>{a.metadata.species_name}</Text>
                  )}
                </div>
              ),
            }))}
          />
        ) : (
          <Text type="secondary">No activity recorded</Text>
        )}
      </Card>
    </div>
  );
};

export default UserDetail;
